import React, { createContext, useCallback, useContext, useRef } from "react";

type NavigationGuard = () => Promise<boolean> | boolean;

interface UnsavedNavigationContextValue {
  confirmNavigation: () => Promise<boolean>;
  registerNavigationGuard: (guard: NavigationGuard) => () => void;
}

const UnsavedNavigationContext = createContext<UnsavedNavigationContextValue | undefined>(undefined);

export function UnsavedNavigationProvider({ children }: { children: React.ReactNode }) {
  const guardRef = useRef<NavigationGuard | null>(null);

  const registerNavigationGuard = useCallback((guard: NavigationGuard) => {
    guardRef.current = guard;

    return () => {
      if (guardRef.current === guard) {
        guardRef.current = null;
      }
    };
  }, []);

  const confirmNavigation = useCallback(async () => {
    if (!guardRef.current) {
      return true;
    }

    return Boolean(await guardRef.current());
  }, []);

  return (
    <UnsavedNavigationContext.Provider value={{ confirmNavigation, registerNavigationGuard }}>
      {children}
    </UnsavedNavigationContext.Provider>
  );
}

export function useUnsavedNavigation() {
  const ctx = useContext(UnsavedNavigationContext);

  if (!ctx) {
    throw new Error("useUnsavedNavigation must be used within UnsavedNavigationProvider");
  }

  return ctx;
}
